// Activity tracking utility for VS Code-like recent activities

const ACTIVITY_STORAGE_KEY = 'item4_recent_activities';
const MAX_ACTIVITIES_PER_MODULE = 5;

export const ActivityTypes = {
  INVENTORY_SEARCH: 'inventory_search',
  ORDER_VIEW: 'order_view',
  ORDER_STATUS_CHANGE: 'order_status_change',
  SMART_CAPTURE: 'smart_capture',
};

export const ModuleNames = {
  [ActivityTypes.INVENTORY_SEARCH]: 'Estoque',
  [ActivityTypes.ORDER_VIEW]: 'Pedidos',
  [ActivityTypes.ORDER_STATUS_CHANGE]: 'Pedidos',
  [ActivityTypes.SMART_CAPTURE]: 'Captura inteligente',
};

export const ActivityIcons = {
  [ActivityTypes.INVENTORY_SEARCH]: '🔍',
  [ActivityTypes.ORDER_VIEW]: '👀',
  [ActivityTypes.ORDER_STATUS_CHANGE]: '🔄',
  [ActivityTypes.SMART_CAPTURE]: '🤖',
};

export const ActivityLabels = {
  [ActivityTypes.INVENTORY_SEARCH]: 'Busca de Produto',
  [ActivityTypes.ORDER_VIEW]: 'Visualização de Pedido',
  [ActivityTypes.ORDER_STATUS_CHANGE]: 'Alteração de Status',
  [ActivityTypes.SMART_CAPTURE]: 'Upload de Arquivo',
};

class ActivityTracker {
  constructor() {
    this.activities = this.loadActivities();
  }

  loadActivities() {
    try {
      const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading activities:', error);
      return {};
    }
  }

  saveActivities() {
    try {
      localStorage.setItem(ACTIVITY_STORAGE_KEY, JSON.stringify(this.activities));
    } catch (error) {
      console.error('Error saving activities:', error);
    }
  }

  addActivity(type, data) {
    const activity = {
      id: Date.now() + Math.random(),
      type,
      data,
      timestamp: new Date().toISOString(),
      module: ModuleNames[type] || 'Unknown'
    };

    // Initialize module array if it doesn't exist
    if (!this.activities[type]) {
      this.activities[type] = [];
    }

    // Add new activity to the beginning
    this.activities[type].unshift(activity);

    // Keep only the latest 5 activities per module
    this.activities[type] = this.activities[type].slice(0, MAX_ACTIVITIES_PER_MODULE);

    this.saveActivities();
    return activity;
  }

  getRecentActivities() {
    const allActivities = [];
    
    Object.keys(this.activities).forEach(type => {
      this.activities[type].forEach(activity => {
        allActivities.push({
          ...activity,
          icon: ActivityIcons[type],
          label: ActivityLabels[type]
        });
      });
    });

    // Sort by timestamp (most recent first)
    return allActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  getActivitiesByModule() {
    const moduleActivities = {};
    
    Object.keys(this.activities).forEach(type => {
      const moduleName = ModuleNames[type];
      if (!moduleActivities[moduleName]) {
        moduleActivities[moduleName] = [];
      }
      
      this.activities[type].forEach(activity => {
        moduleActivities[moduleName].push({
          ...activity,
          icon: ActivityIcons[type],
          label: ActivityLabels[type]
        });
      });
    });

    // Sort activities within each module by timestamp
    Object.keys(moduleActivities).forEach(module => {
      moduleActivities[module].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    });

    return moduleActivities;
  }

  clearActivities() {
    this.activities = {};
    this.saveActivities();
  }

  clearModuleActivities(moduleType) {
    if (this.activities[moduleType]) {
      delete this.activities[moduleType];
      this.saveActivities();
    }
  }
}

export const activityTracker = new ActivityTracker();

// Helper functions for specific activity types
export const trackInventorySearch = (searchTerm, productCode) => {
  return activityTracker.addActivity(ActivityTypes.INVENTORY_SEARCH, {
    searchTerm,
    productCode,
    description: `${searchTerm}`,
    route: `/inventory?search=${encodeURIComponent(searchTerm)}`
  });
};

export const trackOrderView = (orderId, orderTitle, tenantId) => {
  return activityTracker.addActivity(ActivityTypes.ORDER_VIEW, {
    orderId,
    orderTitle,
    description: `${orderTitle}`,
    route: `/orders?order=${orderId}&tenantId=${tenantId}`
  });
};

export const trackOrderStatusChange = (orderId, orderTitle, oldStatus, newStatus, tenantId) => {
  return activityTracker.addActivity(ActivityTypes.ORDER_STATUS_CHANGE, {
    orderId,
    orderTitle,
    oldStatus,
    newStatus,
    description: `${orderTitle}: ${oldStatus} → ${newStatus}`,
    route: `/orders?order=${orderId}&tenantId=${tenantId}`
  });
};

export const trackSmartCapture = (fileName, fileType) => {
  return activityTracker.addActivity(ActivityTypes.SMART_CAPTURE, {
    fileName,
    fileType,
    description: `${fileName}`,
    route: `/smart-order-capture`
  });
};
